import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Send, Trash2, Calendar, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Avatar({ name, picture, size = 36 }) {
  if (picture) {
    return <img src={picture} alt={name} referrerPolicy="no-referrer"
      className="rounded-full object-cover shrink-0"
      style={{ width: size, height: size }} />;
  }
  return (
    <div className="rounded-full shrink-0 flex items-center justify-center font-bold"
      style={{ width: size, height: size, background: "var(--primary)", color: "white", fontSize: size * 0.4 }}>
      {(name || "?").charAt(0).toUpperCase()}
    </div>
  );
}

export default function ForumThread() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "fr" ? "fr-FR" : "it-IT";
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [posting, setPosting] = useState(false);

  const fmtDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString(dateLocale, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/forum/threads/${id}`, { withCredentials: true });
      setData(r.data);
    } catch {
      toast.error(t("forum.notFoundError"));
      navigate("/forum");
    } finally { setLoading(false); }
  };

    // eslint-disable-next-line
  useEffect(() => { load(); }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setPosting(true);
    try {
      await axios.post(`${API}/forum/posts`, {
        parent_id: id,
        content: reply,
      }, { withCredentials: true });
      setReply("");
      load();
    } catch { toast.error(t("forum.sendError")); }
    finally { setPosting(false); }
  };

  const deletePost = async (postId, isThread) => {
    if (!window.confirm(isThread ? t("forum.deleteThreadConfirm") : t("forum.deleteReplyConfirm"))) return;
    try {
      await axios.delete(`${API}/forum/posts/${postId}`, { withCredentials: true });
      if (isThread) navigate("/forum");
      else load();
      toast.success(t("forum.deleteSuccess"));
    } catch { toast.error(t("forum.deleteError")); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="vs-spinner" /></div>;
  }
  if (!data) return null;

  const { thread, replies } = data;
  const isMine = user?.user_id === thread.user_id;

  return (
    <div className="min-h-screen pb-40" data-testid="thread-page">
      <Header title={t("forum.threadPageTitle")} right={
        <button onClick={() => navigate("/forum")} className="p-2 rounded-lg"
          style={{ border: "2px solid var(--border)" }} aria-label={t("forum.backAriaLabel")} data-testid="thread-back">
          <ArrowLeft size={18} />
        </button>
      } />
      <main className="max-w-2xl mx-auto px-4 py-5 space-y-3">
        {/* Thread root */}
        <article className="vs-card p-4" data-testid="thread-root">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {thread.category && (
              <span className="vs-pill text-xs"
                style={{ background: "var(--grape)", color: "white", borderColor: "transparent" }}>
                {thread.category}
              </span>
            )}
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>
              <Calendar size={11} className="inline mr-1" />{fmtDate(thread.created_at)}
            </span>
            {isMine && (
              <button onClick={() => deletePost(thread.id, true)} className="ml-auto p-1" aria-label={t("forum.deleteAriaLabel")}>
                <Trash2 size={14} style={{ color: "var(--accent)" }} />
              </button>
            )}
          </div>
          <h1 className="heading text-xl font-extrabold mb-2">{thread.title}</h1>
          <div className="flex items-center gap-2 mb-3">
            <Avatar name={thread.author_name} picture={thread.author_picture} size={32} />
            <span className="text-sm font-semibold">{thread.author_name}</span>
          </div>
          <div className="text-sm whitespace-pre-wrap" style={{ color: "var(--text-secondary)", lineHeight: 1.6 }}>
            {thread.content}
          </div>
        </article>

        {/* Replies header */}
        <div className="flex items-center gap-2 mt-4 mb-1">
          <MessageCircle size={18} style={{ color: "var(--primary)" }} />
          <h3 className="heading font-bold">
            {replies.length === 0 ? t("forum.noRepliesYet") : t("forum.replyCount", { count: replies.length })}
          </h3>
        </div>

        {/* Replies */}
        <div className="space-y-2">
          {replies.map((r) => {
            const rMine = user?.user_id === r.user_id;
            return (
              <article key={r.id} className="vs-card p-3 vs-fade-up" data-testid={`reply-${r.id}`}
                style={{ borderLeft: "4px solid var(--primary)" }}>
                <div className="flex items-start gap-2">
                  <Avatar name={r.author_name} picture={r.author_picture} size={32} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-bold text-sm">{r.author_name}</span>
                      <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                        {fmtDate(r.created_at)}
                      </span>
                      {rMine && (
                        <button onClick={() => deletePost(r.id, false)} className="ml-auto p-1" aria-label={t("forum.deleteAriaLabel")}>
                          <Trash2 size={12} style={{ color: "var(--accent)" }} />
                        </button>
                      )}
                    </div>
                    <div className="text-sm whitespace-pre-wrap" style={{ color: "var(--text-secondary)" }}>
                      {r.content}
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </main>

      {/* Reply form - sticky at bottom */}
      <form onSubmit={submit}
        className="fixed bottom-16 left-0 right-0 p-3 z-30"
        style={{ background: "var(--surface)", borderTop: "2px solid var(--border)" }}
        data-testid="reply-form">
        <div className="max-w-2xl mx-auto flex gap-2">
          <input className="vs-input flex-1"
            value={reply} onChange={(e) => setReply(e.target.value)}
            placeholder={t("forum.replyPlaceholder")}
            maxLength={2000}
            data-testid="reply-input" />
          <button type="submit" disabled={posting || !reply.trim()}
            className="vs-btn-primary px-4 inline-flex items-center justify-center"
            style={{ background: "var(--gradient-vine)", borderColor: "var(--primary)" }}
            data-testid="reply-submit">
            <Send size={18} />
          </button>
        </div>
      </form>

      <BottomNav />
    </div>
  );
}
